// ============================================================================
// AetherProxy Dashboard — Router Configuration
// ============================================================================
// Clean, scalable SPA routing for Vite + React + Clerk on Cloudflare Pages.
//
// Sections:
// 1. Imports
// 2. Lazy page chunks
// 3. Route-level dependency injection
// 4. Shared Suspense + route protection + route error UI
// 5. Route tree (with loaders)
// 6. Browser router factory
// ============================================================================

import { Suspense, lazy, type ComponentType, type ReactNode } from 'react';
import {
  createBrowserRouter,
  isRouteErrorResponse,
  useRouteError,
  type RouteObject,
} from 'react-router-dom';

import App from './App';
import type { AdminApiClient } from './api';
import { useAppState } from './app-context';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PageLoading } from './components/PageLoading';
import { ProtectedRoute } from './components/ProtectedRoute';
import {
  logsLoader,
  providersLoader,
  modelsLoader,
  keysLoader,
  routingLoader,
} from './loaders';

// ============================================================================
// 1. Route/page types
// ============================================================================

type ApiPageProps = {
  api: AdminApiClient;
};

type PlaygroundPageProps = ApiPageProps & {
  apiBase: string;
};

type SettingsPageProps = {
  apiBase: string;
  setApiBase: (value: string) => void;
  adminToken: string;
  setAdminToken: (value: string) => void;
  clerkEnabled: boolean;
};

// ============================================================================
// 2. Lazy page chunks
// ============================================================================
// Keep route components lazy-loaded so the initial Cloudflare Pages bundle stays
// small. When pages are later split into one file per route, only this section
// needs to change.

const DashboardPage = lazy<ComponentType<ApiPageProps>>(() =>
  import('./pages').then((module) => ({ default: module.OverviewPage })),
);

const ProvidersPage = lazy<ComponentType<ApiPageProps>>(() =>
  import('./pages').then((module) => ({ default: module.ProvidersPage })),
);

const ModelsPage = lazy<ComponentType<ApiPageProps>>(() =>
  import('./pages').then((module) => ({ default: module.ModelsPage })),
);

const KeysPage = lazy<ComponentType<ApiPageProps>>(() =>
  import('./pages').then((module) => ({ default: module.KeysPage })),
);

const LogsPage = lazy<ComponentType<ApiPageProps>>(() =>
  import('./pages').then((module) => ({ default: module.UsagePage })),
);

const PlaygroundPage = lazy<ComponentType<PlaygroundPageProps>>(() =>
  import('./pages').then((module) => ({ default: module.PlaygroundPage })),
);

const RoutingPage = lazy<ComponentType<ApiPageProps>>(() =>
  import('./pages').then((module) => ({ default: module.RoutingPage })),
);

const SettingsPage = lazy<ComponentType<SettingsPageProps>>(() =>
  import('./pages').then((module) => ({ default: module.SettingsPage })),
);

// ============================================================================
// 3. Route-level dependency injection
// ============================================================================
// App owns dashboard state (api client, Clerk/admin-token mode, settings). Page
// routes read that state from context here, so App remains a pure layout with
// <Outlet /> and pages stay explicit/typed about their dependencies.

function createApiRoute(
  Page: ComponentType<ApiPageProps>,
  displayName: string,
): ComponentType {
  function ApiRoute() {
    const { api } = useAppState();
    return <Page api={api} />;
  }

  ApiRoute.displayName = displayName;
  return ApiRoute;
}

const DashboardRoute = createApiRoute(DashboardPage, 'DashboardRoute');
const ProvidersRoute = createApiRoute(ProvidersPage, 'ProvidersRoute');
const ModelsRoute = createApiRoute(ModelsPage, 'ModelsRoute');
const KeysRoute = createApiRoute(KeysPage, 'KeysRoute');
const LogsRoute = createApiRoute(LogsPage, 'LogsRoute');
const RoutingRoute = createApiRoute(RoutingPage, 'RoutingRoute');

function PlaygroundRoute() {
  const { api, apiBase } = useAppState();
  return <PlaygroundPage api={api} apiBase={apiBase} />;
}
PlaygroundRoute.displayName = 'PlaygroundRoute';

function SettingsRoute() {
  const { apiBase, setApiBase, adminToken, setAdminToken, clerkEnabled } =
    useAppState();

  return (
    <SettingsPage
      apiBase={apiBase}
      setApiBase={setApiBase}
      adminToken={adminToken}
      setAdminToken={setAdminToken}
      clerkEnabled={clerkEnabled}
    />
  );
}
SettingsRoute.displayName = 'SettingsRoute';

// ============================================================================
// 4. Shared Suspense + route protection + route error UI
// ============================================================================
// Suspense handles lazy route chunks. ProtectedRoute is a small Clerk guard for
// admin-only pages (no-op in bootstrap admin-token mode). React Router
// errorElement handles route errors (404/loader/action). The existing
// ErrorBoundary still protects render errors inside both the App layout and the
// route error screen itself.

function RouteSuspense({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

function lazyElement(Route: ComponentType): ReactNode {
  return (
    <RouteSuspense>
      <Route />
    </RouteSuspense>
  );
}

function protectedLazyElement(Route: ComponentType): ReactNode {
  return (
    <ProtectedRoute>
      {lazyElement(Route)}
    </ProtectedRoute>
  );
}

function RouteErrorPage() {
  const error = useRouteErrorSafe();

  return (
    <main
      className="login-page"
      style={{ minHeight: '100vh' }}
      aria-labelledby="route-error-title"
    >
      <section className="login-card" role="alert">
        <div className="brand-mark large">A</div>
        <h1 id="route-error-title">{error.title}</h1>
        <p>{error.message}</p>
        <button className="primary" type="button" onClick={() => window.location.assign('/')}>
          Back to dashboard
        </button>
      </section>
    </main>
  );
}

function RootErrorElement() {
  return (
    <ErrorBoundary>
      <RouteErrorPage />
    </ErrorBoundary>
  );
}

function useRouteErrorSafe(): { title: string; message: string } {
  // Keep production errors sanitized. Detailed messages are useful in local
  // development, but can leak internals when shown to end users.
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    const isClientError = error.status >= 400 && error.status < 500;

    return {
      title: error.status === 404 ? 'Page Not Found' : `Error ${error.status}`,
      message: isClientError
        ? routeErrorMessage(error.data) ?? error.statusText
        : 'The requested route could not be loaded. Please try again.',
    };
  }

  if (error instanceof Error) {
    return {
      title: 'Application Error',
      message: import.meta.env.DEV
        ? error.message
        : 'Something went wrong while loading this route.',
    };
  }

  return {
    title: 'Unexpected Error',
    message: 'Something went wrong while loading this route.',
  };
}

function routeErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object' || !('message' in data)) {
    return undefined;
  }

  const message = String(data.message).trim();
  return message || undefined;
}

function notFoundLoader(): never {
  throw new Response(JSON.stringify({ message: 'Route not found.' }), {
    status: 404,
    statusText: 'Page Not Found',
    headers: { 'Content-Type': 'application/json' },
  });
}

// ============================================================================
// 5. Route tree (with loaders)
// ============================================================================
// Root route uses App as the layout. Child routes are declared in one flat list
// for simple growth and predictable Cloudflare Pages SPA fallback behavior.
//
// Loaders run BEFORE the route component mounts, so data is available via
// `useLoaderData()` on first render — no loading spinner flicker.
// After mount, components use `useFetch` for refreshes and filter changes.

const childRoutes: RouteObject[] = [
  {
    index: true,
    element: protectedLazyElement(DashboardRoute),
  },
  {
    path: 'dashboard',
    element: protectedLazyElement(DashboardRoute),
  },
  {
    path: 'providers',
    element: protectedLazyElement(ProvidersRoute),
    loader: providersLoader,
  },
  {
    path: 'models',
    element: protectedLazyElement(ModelsRoute),
    loader: modelsLoader,
  },
  {
    path: 'keys',
    element: protectedLazyElement(KeysRoute),
    loader: keysLoader,
  },
  {
    path: 'logs',
    element: protectedLazyElement(LogsRoute),
    loader: logsLoader,
  },
  {
    path: 'playground',
    element: protectedLazyElement(PlaygroundRoute),
  },
  {
    path: 'routing',
    element: protectedLazyElement(RoutingRoute),
    loader: routingLoader,
  },
  {
    path: 'settings',
    element: lazyElement(SettingsRoute),
  },
  {
    path: '*',
    loader: notFoundLoader,
  },
];

export const routes: RouteObject[] = [
  {
    id: 'root',
    path: '/',
    element: (
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    ),
    errorElement: <RootErrorElement />,
    children: childRoutes,
  },
];

// ============================================================================
// 6. Browser router factory
// ============================================================================
// createBrowserRouter is correct for a pure SPA on Cloudflare Pages when paired
// with public/_redirects. React Router v7 already includes the stable v6.4+
// future-flag behavior; keep this options object as the single place for safe
// future opt-ins without enabling experimental flags accidentally. basename is
// derived from Vite BASE_URL for subpath deployments, and omitted for root Pages
// deployments.

const basename = routerBasename();

const routerOptions = {
  ...(basename ? { basename } : {}),
  future: {},
} satisfies NonNullable<Parameters<typeof createBrowserRouter>[1]>;

function routerBasename(): string | undefined {
  const baseUrl = import.meta.env.BASE_URL;
  if (!baseUrl || baseUrl === '/') return undefined;
  return `/${baseUrl.replace(/^\/+|\/+$/g, '')}`;
}

let router: ReturnType<typeof createBrowserRouter> | undefined;

export function getRouter() {
  router ??= createBrowserRouter(routes, routerOptions);
  return router;
}

// Backward-compatible factory for older callers/tests.
export function createAppRouter(_clerkEnabled: boolean) {
  return getRouter();
}
